import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import React, { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import HeadingLayout from "src/components/base/heading-layout";
import { Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Listbox, ListboxLabel, ListboxOption } from "src/components/base/listbox";
import { Combobox, ComboboxOption } from "src/components/base/combobox";
import { Button, PrimaryButton } from "src/components/base/button";
import WS from "src/api/ws";
import { ArrowsRightLeftIcon, EyeIcon, TableCellsIcon } from "@heroicons/react/20/solid";
import type {
    AccessGraphEdgeSchema,
    AccessGraphNodeSchema,
    AccessVisualizationEntitySchema,
} from "src/api/generated/frontend_internal_v1";
import { ApiInternal } from "src/api/api_internal";

// Lazy-loaded so @xyflow/react + elkjs (graph) and the dialog tree don't land in the main chunk.
const GraphView = lazy(() => import("src/components/access-visualization/graph-view"));
const MatrixView = lazy(() => import("src/components/access-visualization/matrix-view"));
const CheckAccessDialog = lazy(() => import("src/components/access-visualization/check-access-dialog"));

/** Filter by entity type, or "all" for no filter */
type EntityTypeFilter = "all" | "group" | "tag" | "deviceGroup" | "subnet";
/** Top-level visualization mode */
type ViewMode = "graph" | "matrix";

/** Search params persisted in the URL for this route */
type AccessVisualizationSearch = {
    view: ViewMode;
    entityType: EntityTypeFilter;
    /** Encoded as "Type:uuid" e.g. "Group:abc-123" or "" for no focus */
    entityId: string;
};

const TYPE_MAP: Record<EntityTypeFilter, AccessVisualizationEntitySchema["type"] | undefined> = {
    all: undefined,
    group: "Group",
    tag: "Tag",
    deviceGroup: "DeviceGroup",
    subnet: "Subnet",
};

/**
 * Encode entity type + uuid into a single string.
 *
 * @param type
 * @param uuid
 * @returns the combined "Type:uuid" representation
 */
function encodeEntityId(type: string, uuid: string): string {
    return `${type}:${uuid}`;
}

/** An option in the entity focus combobox */
type EntityOption = {
    encoded: string;
    type: string;
    name: string;
    deviceCount: number;
};

/**
 * Decode "Type:uuid" back into its parts.
 *
 * @param encoded
 * @returns the decoded `{ type, uuid }`, or null if the input is empty/malformed
 */
function decodeEntityId(encoded: string): { type: string; uuid: string } | null {
    const idx = encoded.indexOf(":");
    if (idx < 1) return null;
    return { type: encoded.slice(0, idx), uuid: encoded.slice(idx + 1) };
}

/**
 * Page component for the access visualization route.
 *
 * @returns the rendered page
 */
function AccessVisualizationPage() {
    const [t] = useTranslation("admin-access-visualization");

    const navigate = useNavigate();
    const router = useRouter();
    const { view, entityType, entityId } = Route.useSearch();
    const data = Route.useLoaderData();

    const [checkAccessOpen, setCheckAccessOpen] = useState(false);
    const [entitySearch, setEntitySearch] = useState("");

    const decoded = useMemo(() => decodeEntityId(entityId), [entityId]);

    useEffect(() => {
        const listeners = [
            WS.addEventListener("message.TagsChanged", () => router.invalidate({ sync: true })),
            WS.addEventListener("message.GroupsChanged", () => router.invalidate({ sync: true })),
            WS.addEventListener("message.DeviceGroupsChanged", () => router.invalidate({ sync: true })),
            WS.addEventListener("message.SubnetsChanged", () => router.invalidate({ sync: true })),
        ];
        return () => listeners.forEach((l) => WS.removeEventListener(l));
    }, []);

    // Apply client-side filters: entityType + search
    const filteredData = useMemo(() => {
        if (!data) return { nodes: [] as AccessGraphNodeSchema[], edges: [] as AccessGraphEdgeSchema[] };

        let nodes: AccessGraphNodeSchema[] = data.nodes;
        let edges: AccessGraphEdgeSchema[] = data.edges;

        // Filter by entity type (when no specific entity is focused)
        if (entityType !== "all") {
            const targetType = TYPE_MAP[entityType];
            const typeNodes = nodes.filter((n) => n.entity.type === targetType);
            const typeNodeKeys = new Set(typeNodes.map((n) => JSON.stringify(n.entity)));

            const connectedKeys = new Set<string>();
            for (const e of edges) {
                const srcKey = JSON.stringify(e.src);
                const dstKey = JSON.stringify(e.dst);
                if (typeNodeKeys.has(srcKey)) connectedKeys.add(dstKey);
                if (typeNodeKeys.has(dstKey)) connectedKeys.add(srcKey);
            }

            const allRelevantKeys = new Set([...typeNodeKeys, ...connectedKeys]);
            nodes = nodes.filter((n) => allRelevantKeys.has(JSON.stringify(n.entity)));
            edges = edges.filter(
                (e) => typeNodeKeys.has(JSON.stringify(e.src)) || typeNodeKeys.has(JSON.stringify(e.dst)),
            );
        }

        if (decoded) {
            const typeNodeKey = JSON.stringify({ type: decoded.type, value: decoded.uuid });

            const connectedKeys = new Set<string>();
            for (const e of edges) {
                const srcKey = JSON.stringify(e.src);
                const dstKey = JSON.stringify(e.dst);
                if (srcKey === typeNodeKey) connectedKeys.add(dstKey);
                if (dstKey === typeNodeKey) connectedKeys.add(srcKey);
            }

            const allRelevantKeys = new Set([...connectedKeys, typeNodeKey]);
            nodes = nodes.filter((n) => allRelevantKeys.has(JSON.stringify(n.entity)));
            edges = edges.filter((e) => e.src.value === decoded.uuid || e.dst.value === decoded.uuid);
        }

        return { nodes, edges };
    }, [data, entityType, decoded]);

    // Entity options for the focus dropdown — filtered by entityType
    const entityOptions = useMemo<EntityOption[]>(() => {
        if (!data) return [];
        return data.nodes
            .filter((n) => entityType === "all" || n.entity.type === TYPE_MAP[entityType])
            .map((n) => ({
                encoded: encodeEntityId(n.entity.type, n.entity.value),
                type: n.entity.type,
                name: n.name,
                deviceCount: n.device_count,
            }))
            .sort((a, b) => {
                if (a.type !== b.type) return a.type.localeCompare(b.type);
                return a.name.localeCompare(b.name);
            });
    }, [data, entityType]);

    const selectedEntity = entityOptions.find((opt) => opt.encoded === entityId) ?? null;
    const filteredEntityOptions =
        entitySearch === ""
            ? entityOptions
            : entityOptions.filter((opt) => opt.name.toLowerCase().includes(entitySearch.toLowerCase()));

    const doNavigate = async (params: Partial<AccessVisualizationSearch>) => {
        await navigate({
            to: "/a/access-visualization",
            search: { view, entityType, entityId, ...params },
        });
    };

    return (
        <HeadingLayout
            heading={t("heading.access-visualization")}
            headingDescription={t("description.access-visualization")}
            headingChildren={
                <div className={"flex gap-2"}>
                    {view === "graph" ? (
                        <Button color={"blue"} onClick={() => {}}>
                            <EyeIcon />
                            {t("label.graph-view")}
                        </Button>
                    ) : (
                        <Button outline onClick={() => doNavigate({ view: "graph" })}>
                            <EyeIcon />
                            {t("label.graph-view")}
                        </Button>
                    )}
                    {view === "matrix" ? (
                        <Button color={"blue"} onClick={() => {}}>
                            <TableCellsIcon />
                            {t("label.matrix-view")}
                        </Button>
                    ) : (
                        <Button outline onClick={() => doNavigate({ view: "matrix" })}>
                            <TableCellsIcon />
                            {t("label.matrix-view")}
                        </Button>
                    )}
                    <PrimaryButton onClick={() => setCheckAccessOpen(true)}>
                        <ArrowsRightLeftIcon />
                        {t("label.check-access")}
                    </PrimaryButton>
                </div>
            }
        >
            {/* Filters */}
            <div className={"flex flex-wrap items-end gap-6"}>
                {/* Entity type filter */}
                <Fieldset className={"w-48"}>
                    <FieldGroup>
                        <Field>
                            <Label>{t("label.filter-entity-type")}</Label>
                            <Listbox
                                value={entityType}
                                onChange={(x) => doNavigate({ entityType: x as EntityTypeFilter, entityId: "" })}
                            >
                                <ListboxOption value={"all"}>
                                    <ListboxLabel>{t("label.filter-all")}</ListboxLabel>
                                </ListboxOption>
                                <ListboxOption value={"group"}>
                                    <ListboxLabel>{t("label.filter-groups")}</ListboxLabel>
                                </ListboxOption>
                                <ListboxOption value={"tag"}>
                                    <ListboxLabel>{t("label.filter-tags")}</ListboxLabel>
                                </ListboxOption>
                                <ListboxOption value={"deviceGroup"}>
                                    <ListboxLabel>{t("label.filter-device-groups")}</ListboxLabel>
                                </ListboxOption>
                                <ListboxOption value={"subnet"}>
                                    <ListboxLabel>{t("label.filter-subnets")}</ListboxLabel>
                                </ListboxOption>
                            </Listbox>
                        </Field>
                    </FieldGroup>
                </Fieldset>

                {/* Entity focus selector */}
                <Fieldset className={"min-w-64 flex-1"}>
                    <FieldGroup>
                        <Field>
                            <Label>{t("label.filter-entity")}</Label>
                            <Combobox
                                value={selectedEntity}
                                onChange={(opt) => doNavigate({ entityId: opt?.encoded ?? "" })}
                                onClose={() => setEntitySearch("")}
                                queryDisplay={(opt) => opt?.name ?? ""}
                                onQueryChange={setEntitySearch}
                            >
                                {[
                                    ...(entitySearch === "" || entitySearch === "-"
                                        ? [
                                              <ComboboxOption key={"-"} value={null}>
                                                  -
                                              </ComboboxOption>,
                                          ]
                                        : []),
                                    ...filteredEntityOptions.map((opt) => {
                                        const label =
                                            opt.type === "Group"
                                                ? t("label.group")
                                                : opt.type === "Tag"
                                                  ? t("label.tag")
                                                  : opt.type === "DeviceGroup"
                                                    ? t("label.device-group")
                                                    : opt.type === "Subnet"
                                                      ? t("label.subnet")
                                                      : "";
                                        return (
                                            <ComboboxOption key={opt.encoded} value={opt}>
                                                {opt.name}{" "}
                                                <span className="text-zinc-500 dark:text-zinc-400">
                                                    (
                                                    {opt.deviceCount === 1
                                                        ? t("label.device-count", {
                                                              label: label,
                                                              count: opt.deviceCount,
                                                          })
                                                        : t("label.device-counts", {
                                                              label: label,
                                                              count: opt.deviceCount,
                                                          })}
                                                    )
                                                </span>
                                            </ComboboxOption>
                                        );
                                    }),
                                ]}
                            </Combobox>
                        </Field>
                    </FieldGroup>
                </Fieldset>
            </div>

            {/* Main visualization area */}
            <div
                className={
                    "mt-4 h-[calc(100vh-300px)] min-h-100 w-full rounded-lg border border-zinc-200 dark:border-zinc-700"
                }
            >
                <Suspense
                    fallback={
                        <div className="flex h-full items-center justify-center text-zinc-500 dark:text-zinc-400">
                            Loading...
                        </div>
                    }
                >
                    {view === "graph" ? (
                        <GraphView
                            nodes={filteredData.nodes}
                            edges={filteredData.edges}
                            focusEntityId={decoded?.uuid}
                        />
                    ) : (
                        <MatrixView nodes={filteredData.nodes} edges={filteredData.edges} />
                    )}
                </Suspense>
            </div>

            {checkAccessOpen && (
                <Suspense fallback={null}>
                    <CheckAccessDialog isOpen={checkAccessOpen} onClose={() => setCheckAccessOpen(false)} />
                </Suspense>
            )}
        </HeadingLayout>
    );
}

export const Route = createFileRoute("/_admin/a/access-visualization/")({
    component: AccessVisualizationPage,
    validateSearch: (search: Record<string, unknown>): AccessVisualizationSearch => ({
        view: ((search.view as string) || "graph") as ViewMode,
        entityType: ((search.entityType as string) || "all") as EntityTypeFilter,
        entityId: (search.entityId as string) || "",
    }),
    loaderDeps: ({ search: { entityId } }) => ({ entityId }),
    loader: async () => await ApiInternal.accessVisualization.getGraph(),
});
